import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { useDispatch } from 'react-redux'

import type { MenuURN, RecipeURN } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { actionTypes } from 'actions/actionTypes'
import * as trackingKeys from 'actions/trackingKeys'

type RecipeDetailSource = 'basket' | 'recipe_grid' | 'merch_banner'

export type MenuExperienceRecipeDetail = {
  initialFlexPortionEditorOpen?: boolean
  recipeId: RecipeUUID
  recipeUrn?: RecipeURN
  title: string
  position?: string
  promotionId?: string
  source: RecipeDetailSource
  merchHeaderType?: string
  onRecipePortionsUpdated?: (recipeId: RecipeUUID, numPortions: number) => void
}

type OpenRecipeDetailInput = MenuExperienceRecipeDetail

type MenuExperienceRecipeDetailContextValue = {
  menuId: string
  menuUrn?: MenuURN
  recipeDetail: MenuExperienceRecipeDetail | null
  openRecipeDetail: (recipeDetail: OpenRecipeDetailInput) => void
  closeRecipeDetail: () => void
  selectRecipe: (recipeId: RecipeUUID, recipeUrn?: RecipeURN) => void
  updateRecipePortions: (recipeId: RecipeUUID, numPortions: number) => void
}

type MenuExperienceRecipeDetailProviderProps = {
  children: React.ReactNode
  collectionId?: string
  collectionSlug?: string
  menuId: string
  menuUrn?: MenuURN
  updateRecipePortions: MenuExperienceRecipeDetailContextValue['updateRecipePortions']
}

type ViewRecipeDetailsEvent = {
  action: 'view_recipe_details'
  properties: {
    menu_id: string
    recipe_id: string
    source: RecipeDetailSource
    position?: string
    merch_header_type?: string
    promotion_banner_displayed: boolean
    promotion_id?: string
  }
}

const MenuExperienceRecipeDetailContext = createContext<
  MenuExperienceRecipeDetailContextValue | undefined
>(undefined)

export const MenuExperienceRecipeDetailProvider = ({
  children,
  collectionId,
  collectionSlug,
  menuId,
  menuUrn,
  updateRecipePortions,
}: MenuExperienceRecipeDetailProviderProps) => {
  const dispatch = useDispatch()
  const [recipeDetail, setRecipeDetail] = useState<MenuExperienceRecipeDetail | null>(null)

  const openRecipeDetail = useCallback(
    (nextRecipeDetail: OpenRecipeDetailInput) => {
      setRecipeDetail(nextRecipeDetail)

      queueTrackingEvent<ViewRecipeDetailsEvent>({
        action: 'view_recipe_details',
        properties: {
          menu_id: menuId,
          recipe_id: nextRecipeDetail.recipeId,
          source: nextRecipeDetail.source,
          ...(nextRecipeDetail.position ? { position: nextRecipeDetail.position } : {}),
          ...(nextRecipeDetail.merchHeaderType
            ? { merch_header_type: nextRecipeDetail.merchHeaderType }
            : {}),
          promotion_banner_displayed: Boolean(nextRecipeDetail.promotionId),
          ...(nextRecipeDetail.promotionId ? { promotion_id: nextRecipeDetail.promotionId } : {}),
        },
      })
      dispatch({
        type: actionTypes.MENU_RECIPE_DETAIL_VISIBILITY_CHANGE,
        recipeUuid: nextRecipeDetail.recipeId,
        trackingData: {
          actionType: trackingKeys.changeMenuRecipeDetailVisibility,
          menu_uuid: menuId,
          collection_id: collectionId,
          ...(collectionSlug && { collection_slug: collectionSlug }),
          ...(nextRecipeDetail.source === 'merch_banner' && {
            source: nextRecipeDetail.source,
            ...(nextRecipeDetail.merchHeaderType && {
              merch_header_type: nextRecipeDetail.merchHeaderType,
            }),
          }),
          recipeUuid: nextRecipeDetail.recipeId,
          show: true,
        },
      })
      datadogRum.addAction('open_menu_recipe_detail', {
        menu_id: menuId,
        recipe_id: nextRecipeDetail.recipeId,
        source: nextRecipeDetail.source,
        ...(nextRecipeDetail.merchHeaderType
          ? { merch_header_type: nextRecipeDetail.merchHeaderType }
          : {}),
      })
    },
    [collectionId, collectionSlug, dispatch, menuId],
  )

  const closeRecipeDetail = useCallback(() => {
    setRecipeDetail(null)
    dispatch({
      type: actionTypes.MENU_RECIPE_DETAIL_VISIBILITY_CHANGE,
      recipeUuid: undefined,
      trackingData: {
        actionType: trackingKeys.changeMenuRecipeDetailVisibility,
        menu_uuid: menuId,
        collection_id: collectionId,
        ...(collectionSlug && { collection_slug: collectionSlug }),
        recipeUuid: undefined,
        show: false,
      },
    })
  }, [collectionId, collectionSlug, dispatch, menuId])

  const selectRecipe = useCallback(
    (recipeId: RecipeUUID, recipeUrn?: RecipeURN) => {
      if (recipeDetail) {
        openRecipeDetail({ ...recipeDetail, recipeId, recipeUrn, promotionId: undefined })
      }
    },
    [openRecipeDetail, recipeDetail],
  )

  const value = useMemo(
    () => ({
      menuId,
      menuUrn,
      recipeDetail,
      openRecipeDetail,
      closeRecipeDetail,
      selectRecipe,
      updateRecipePortions,
    }),
    [
      closeRecipeDetail,
      menuId,
      menuUrn,
      openRecipeDetail,
      recipeDetail,
      selectRecipe,
      updateRecipePortions,
    ],
  )

  return (
    <MenuExperienceRecipeDetailContext.Provider value={value}>
      {children}
    </MenuExperienceRecipeDetailContext.Provider>
  )
}

export const useMenuExperienceRecipeDetails = () => {
  const context = useContext(MenuExperienceRecipeDetailContext)

  if (!context) {
    throw new Error(
      'useMenuExperienceRecipeDetails must be used within MenuExperienceRecipeDetailProvider',
    )
  }

  return context
}
